import React from 'react';
import ThemeProvider from './common/ThemeProvider';
import MatchCard from './MatchCard/MatchCard';
import MatchInfo from './MatchInfo/MatchInfo';
import GoalsList from './GoalsList/GoalsList';
import LiveScores from './LiveScores/LiveScores';
import LiveMatches from './LiveMatches/LiveMatches';
import Fixtures from './Fixtures/Fixtures';
import Results from './Results/Results';
import Standings from './Standings/Standings';
import GroupStandings from './GroupStandings/GroupStandings';
import FormGuide from './FormGuide/FormGuide';
import TeamStats from './TeamStats/TeamStats';
import TopScorers from './TopScorers/TopScorers';
import TopAssists from './TopAssists/TopAssists';
import Appearances from './Appearances/Appearances';
import Minutes from './Minutes/Minutes';
import Discipline from './Discipline/Discipline';
import Injuries from './Injuries/Injuries';
import MatchEvents from './MatchEvents/MatchEvents';
import Lineups from './Lineups/Lineups';
import TeamList from './TeamList/TeamList';
import TeamProfile from './TeamProfile/TeamProfile';
import PlayerList from './PlayerList/PlayerList';
import PlayerProfile from './PlayerProfile/PlayerProfile';
import CoachCard from './CoachCard/CoachCard';
import { WidgetLabelsProvider } from './common/WidgetLabelsContext';
import { useWidgetData } from '../hooks/useWidgetData';
import { getMockCompetitionData } from '../data/mockCompetitionData';
import { normalizeWidgetType, FEATURED_MATCH_TYPES } from '../builder/widgetTypes';
import { formatMatchOption } from '../builder/matchPickerLabels';
import { useMatchList } from '../hooks/useMatchList';

const WIDGET_MAP = {
  'match-card': MatchCard,
  'match-info': MatchInfo,
  'goals-list': GoalsList,
  'match-events': MatchEvents,
  lineups: Lineups,
  'live-scores': LiveScores,
  'live-matches': LiveMatches,
  fixtures: Fixtures,
  results: Results,
  standings: Standings,
  'group-standings': GroupStandings,
  'form-guide': FormGuide,
  'team-stats': TeamStats,
  'top-scorers': TopScorers,
  'top-assists': TopAssists,
  appearances: Appearances,
  minutes: Minutes,
  discipline: Discipline,
  injuries: Injuries,
  'team-list': TeamList,
  'team-profile': TeamProfile,
  'team-card': TeamProfile,
  'player-list': PlayerList,
  'player-profile': PlayerProfile,
  'player-card': PlayerProfile,
  'coach-card': CoachCard,
};

function WidgetSkeleton() {
  return (
    <div className="p-8 animate-pulse" style={{ background: 'var(--sw-surface)' }}>
      <div className="h-4 bg-slate-200 rounded w-1/3 mb-4 mx-auto" />
      <div className="h-8 bg-slate-200 rounded w-2/3 mb-6 mx-auto" />
      <div className="h-2 bg-slate-200 rounded w-full mb-4" />
      <div className="h-16 bg-slate-200 rounded w-full" />
    </div>
  );
}

function previewBanner(config, api) {
  if (!api || api._mock) return null;
  const labels = {
    standings: null,
    'group-standings': 'Group tables',
    'form-guide': 'Form guide',
    'team-stats': 'Team goals',
    'team-list': 'Team list',
    'team-profile': 'Team profile',
    'player-list': 'Player list',
    'player-profile': 'Player profile',
    'top-scorers': 'Top scorers',
    'top-assists': 'Top assists',
    appearances: 'Appearances',
    minutes: 'Minutes played',
    discipline: 'Cards',
    injuries: 'Squad injuries',
    'match-events': 'Match timeline',
    'goals-list': 'Goals',
    'match-info': 'Match details',
    lineups: 'Lineups & subs',
    fixtures: 'Upcoming',
    results: 'Results',
    'live-matches': 'Live now',
  };
  if (labels[config.type] !== undefined) return labels[config.type];
  if (config.type === 'match-card') {
    if (api.featuredMatch?.status === 'upcoming') return 'Next fixture';
    if (api.featuredMatch?.status === 'finished') return 'Recent result';
    return 'Featured match';
  }
  return api.isSchedule ? 'Upcoming fixtures' : 'Live scores';
}

export default function WidgetPreview({ config, showBranding }) {
  const widgetType = normalizeWidgetType(config.type);
  const hasSelectedMatch = Boolean(config.featuredMatchId);
  const usesFeaturedMatch = FEATURED_MATCH_TYPES.has(widgetType);
  const { data, isLoading, isError, error, isFetching } = useWidgetData({
    ...config,
    type: widgetType,
  });
  const { data: matchListData } = useMatchList(
    config.competitionId,
    usesFeaturedMatch
  );
  const Component = WIDGET_MAP[widgetType] ?? MatchCard;

  const mock = getMockCompetitionData(config.competitionId);
  const payload = data || null;
  const api = payload && !payload._mock ? payload : null;
  const useMockFallback = !hasSelectedMatch && (!payload || isError);
  const waitingForSelectedMatch =
    hasSelectedMatch && (isLoading || isFetching) && !api?.featuredMatch;

  const selectedMatchRow = (matchListData?.matches || []).find(
    (m) => String(m.matchId) === String(config.featuredMatchId)
  );

  const featuredMatch = api?.featuredMatch || (useMockFallback ? mock.featuredMatch : null);

  const previewMismatch =
    hasSelectedMatch &&
    selectedMatchRow &&
    featuredMatch &&
    !featuredMatch.matchNotFound &&
    (featuredMatch.home !== selectedMatchRow.home ||
      featuredMatch.away !== selectedMatchRow.away);
  const events =
    api?.events ||
    featuredMatch?.events ||
    mock.events ||
    mock.goals ||
    featuredMatch?.goals ||
    [];

  const widgetProps = {
    showBranding,
    competitionLabel: api?.competitionLabel || mock.label,
    featuredMatch,
    matches: api?.matches || (useMockFallback ? mock.matches : []),
    table: api?.table || (useMockFallback ? mock.table : []),
    groups: api?.groups ?? (useMockFallback ? mock.groups : null) ?? null,
    formRows: api?.formRows || (useMockFallback ? mock.formRows : []),
    teamStats: api?.teamStats || (useMockFallback ? mock.teamStats : []),
    teams: api?.teams || (useMockFallback ? mock.teams || mock.table : []),
    scorers: api?.scorers || (useMockFallback ? mock.scorers : []),
    players:
      payload?.players ??
      (widgetType === 'player-list'
        ? config.selectedTeam
          ? mock.squads?.[config.selectedTeam] || []
          : []
        : useMockFallback
          ? mock.scorers
          : []),
    listMode: payload?.listMode || (widgetType === 'player-list' ? 'squad' : 'scorers'),
    assists: api?.assists || (useMockFallback ? mock.assists : []),
    appearances: api?.appearances || (useMockFallback ? mock.appearances : []),
    minutes: api?.minutes || (useMockFallback ? mock.minutes : []),
    discipline: api?.discipline || (useMockFallback ? mock.discipline : []),
    injuries: api?.injuries || (useMockFallback ? mock.injuries : []),
    selectedTeam: config.selectedTeam || api?.selectedTeam || null,
    teamLogo: payload?.teamLogo || null,
    teamLogoProxy: payload?.teamLogoProxy || null,
    events,
    goals: api?.goals || (useMockFallback ? mock.goals : []) || featuredMatch?.goals || [],
    lineups: api?.lineups ?? featuredMatch?.lineups ?? (useMockFallback ? mock.lineups : null),
    substitutions:
      api?.substitutions ||
      featuredMatch?.substitutions ||
      (useMockFallback ? mock.substitutions : []),
    teamProfile: api?.teamProfile || (useMockFallback ? mock.teamProfile : null),
    playerProfile:
      api?.playerProfile ||
      (useMockFallback ? mock.playerProfile || mock.scorers?.[0] : null),
  };

  const banner = previewBanner({ ...config, type: widgetType }, api);

  return (
    <WidgetLabelsProvider locale={config.locale || 'en'}>
      <ThemeProvider theme={config.theme} primaryColor={config.primaryColor}>
        {(isLoading && !data) || waitingForSelectedMatch ? (
          <WidgetSkeleton />
        ) : (
          <>
            {previewMismatch && (
              <p className="text-xs text-amber-800 bg-amber-50 px-3 py-2 border-b border-amber-100">
                Preview is out of sync — re-select the match or wait for refresh.
              </p>
            )}
            {hasSelectedMatch &&
              selectedMatchRow &&
              featuredMatch &&
              !featuredMatch.matchNotFound &&
              !previewMismatch && (
              <p className="text-[10px] text-brand-800 bg-brand-50 px-3 py-1.5 border-b border-brand-100 font-semibold">
                Preview: {formatMatchOption(selectedMatchRow)}
              </p>
            )}
            {hasSelectedMatch && api?.featuredMatch?.matchNotFound && (
              <p className="text-xs text-amber-800 bg-amber-50 px-3 py-2 border-b border-amber-100">
                Selected match is not in the feed. Pick another fixture from the list.
              </p>
            )}
            {isError && (
              <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 border-b border-amber-100">
                Live data unavailable: {error?.message}.{' '}
                {hasSelectedMatch ? 'Could not load this match.' : 'Showing sample data.'}
              </p>
            )}
            {!isError && isFetching && api && (
              <p className="text-[10px] text-slate-400 px-3 py-1 border-b border-slate-100">
                Updating…
              </p>
            )}
            {!isError && banner && !hasSelectedMatch && (
              <p className="text-[10px] text-slate-400 px-3 py-1 border-b border-slate-100">
                {banner}
              </p>
            )}
            <Component {...widgetProps} />
          </>
        )}
      </ThemeProvider>
    </WidgetLabelsProvider>
  );
}
